import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiArrowLeft,
  FiDollarSign,
  FiHome,
  FiLoader,
  FiCheckCircle,
  FiClock,
} from "react-icons/fi";

import {
  prePayAutoPool,
  prepayeligible,
  resetPrePayAutoPoolStatus,
  resetPrepayEligible,
  clearPoolError,
} from "../../store/slices/pool";

const PrePayAutoPool = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    prePayAutoPoolLoading,
    prePayAutoPoolSuccess,
    prePayAutoPoolResponse,

    prepayEligible,
    prepayEligibleLoading,

    error,
  } = useSelector((state) => state.pool);

  const [paidPool, setPaidPool] = useState("");
  const [remarks, setRemarks] = useState("");

  /* =====================================================
     GET ELIGIBLE PRE-PAY POOLS
  ===================================================== */

  useEffect(() => {
    if (!userId) return;

    dispatch(prepayeligible({ userId }));

    return () => {
      dispatch(resetPrepayEligible());
    };
  }, [dispatch, userId]);

  /* =====================================================
     RESET STATUS
  ===================================================== */

  useEffect(() => {
    dispatch(resetPrePayAutoPoolStatus());
    dispatch(clearPoolError());

    return () => {
      dispatch(resetPrePayAutoPoolStatus());
      dispatch(clearPoolError());
    };
  }, [dispatch]);

  /* =====================================================
     API DATA
  ===================================================== */

  const currentPool = Number(
    prepayEligible?.currentPool || 0
  );

  const eligiblePools = Array.isArray(
    prepayEligible?.eligiblePools
  )
    ? prepayEligible.eligiblePools
    : [];

  /* =====================================================
     SELECTED POOL DATA
  ===================================================== */

  const selectedPool = eligiblePools.find(
    (item) => Number(item.triggerPool) === Number(paidPool)
  );

  const amount = Number(selectedPool?.rewardAmount || 0);

  /* =====================================================
     SUCCESS
  ===================================================== */

  useEffect(() => {
    if (!prePayAutoPoolSuccess) {
      return;
    }

    const timer = setTimeout(() => {
      navigate(-1);
    }, 1200);

    return () => clearTimeout(timer);
  }, [prePayAutoPoolSuccess, navigate]);

  /* =====================================================
     SELECT PAID POOL
  ===================================================== */

  const handlePaidPoolSelect = (item) => {
    if (prePayAutoPoolLoading) return;

    if (
      item?.alreadyPaid ||
      item?.prepaid ||
      item?.eligibilityStatus !== "ELIGIBLE"
    ) {
      return;
    }

    setPaidPool(String(item.triggerPool));

    dispatch(clearPoolError());
    dispatch(resetPrePayAutoPoolStatus());
  };
  /* =====================================================
     SUBMIT
  ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    dispatch(clearPoolError());
    dispatch(resetPrePayAutoPoolStatus());

    if (!userId || !currentPool || !selectedPool) return;

    if (
      selectedPool.alreadyPaid ||
      selectedPool.prepaid ||
      selectedPool.eligibilityStatus !== "ELIGIBLE"
    ) {
      return;
    }

    if (!amount || amount <= 0) return;

    const triggerPool = Number(selectedPool.triggerPool);
    const previousPool = Number(selectedPool.paidPool);

    if (triggerPool <= previousPool) return;

    const payload = {
      userId,
      triggerPool,
      paidPool: previousPool,
      pool: triggerPool,
      amount,
      remarks:
        remarks.trim() ||
        `Auto Pool Pre-Payment - Pool ${previousPool} -> Pool ${triggerPool}`,
    };

    dispatch(prePayAutoPool(payload));
  };

  /* =====================================================
     LOADING
  ===================================================== */

  if (prepayEligibleLoading) {
    return (
      <div className="container-fluid py-5">
        <div className="d-flex justify-content-center align-items-center gap-2">
          <FiLoader className="spin" />
          <span>Loading eligible pools...</span>
        </div>
      </div>
    );
  }

  /* =====================================================
     UI
  ===================================================== */

  return (
    <div className="container-fluid py-4">

      {/* ================= BREADCRUMB ================= */}

      <div className="d-flex justify-content-between align-items-center mb-4">

        <div className="gi-breadcrumb d-flex align-items-center gap-2">

          <Link
            to="/dashboard"
            className="text-decoration-none d-flex align-items-center gap-1"
          >
            <FiHome size={16} />
            Dashboard
          </Link>

          <span>/</span>

          <Link
            to="/dashboard/autopool"
            className="text-decoration-none"
          >
            Auto Pool
          </Link>

          <span>/</span>

          <span>Pre-Pay Auto Pool</span>

        </div>

        <button
          type="button"
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          onClick={() => navigate(-1)}
          disabled={prePayAutoPoolLoading}
        >
          <FiArrowLeft />
          Back
        </button>

      </div>

      {/* ================= CARD ================= */}

      <div className="card border-0 shadow-sm">

        <div className="card-header bg-white border-bottom py-3">

          <h5 className="mb-0 d-flex align-items-center gap-2">
            <FiDollarSign />
            Pre-Pay Auto Pool
          </h5>

        </div>

        <div className="card-body">

          {/* ================= ERROR ================= */}

          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          {/* ================= SUCCESS ================= */}

          {prePayAutoPoolSuccess && (
            <div className="alert alert-success">
              {prePayAutoPoolResponse?.message ||
                "Auto Pool pre-payment successful"}
            </div>
          )}

          {/* ================= USER / CURRENT POOL ================= */}

          <div className="row">

            <div className="col-md-6 mb-3">

              <label className="form-label">
                User ID
              </label>

              <input
                type="text"
                className="form-control"
                value={userId || ""}
                readOnly
              />

            </div>

            <div className="col-md-6 mb-3">

              <label className="form-label">
                Current Auto Pool
              </label>

              <input
                type="text"
                className="form-control fw-semibold"
                value={
                  currentPool
                    ? `Pool ${currentPool}`
                    : "No Pool"
                }
                readOnly
              />

            </div>

          </div>

          {/* ================= ELIGIBLE POOLS ================= */}

          <div className="mb-4">

            <label className="form-label fw-semibold">
              Eligible Pre-Payment Pools
            </label>

            <small className="text-muted d-block mb-3">
              Select the pool you want to pre-pay.
            </small>

            {eligiblePools.length === 0 ? (
              <div className="alert alert-warning mb-0">
                <FiClock className="me-2" />
                No eligible pools available for pre-payment.
              </div>
            ) : (
              <div className="row g-3">
                {eligiblePools.map((item) => {
                  const isSelected =
                    Number(paidPool) === Number(item.triggerPool);

                  const isAlreadyPaid = item.alreadyPaid === true;
                  const isPrepaid = item.prepaid === true;

                  const isDisabled =
                    isAlreadyPaid ||
                    isPrepaid ||
                    item.eligibilityStatus !== "ELIGIBLE";

                  return (
                    <div
                      className="col-md-4"
                      key={`${item.triggerPool}-${item.paidPool}`}
                    >
                      <button
                        type="button"
                        className={`w-100 text-start border rounded p-3 ${isSelected
                            ? "border-primary bg-light"
                            : "border-light-subtle bg-white"
                          }`}
                        style={{
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.65 : 1,
                        }}
                        onClick={() => handlePaidPoolSelect(item)}
                        disabled={prePayAutoPoolLoading || isDisabled}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-semibold">
                            Pool {item.triggerPool} → Pool {item.paidPool}
                          </span>

                          {isDisabled ? (
                            <FiCheckCircle className="text-success" size={18} />
                          ) : (
                            <FiDollarSign size={18} />
                          )}
                        </div>

                        <div className="text-muted small">Reward Amount</div>

                        <div className="fs-5 fw-bold mt-1">
                          ${Number(item.rewardAmount || 0).toFixed(2)}
                        </div>

                        <div className="mt-2">
                          {isAlreadyPaid ? (
                            <span className="badge bg-secondary">
                              Already Paid
                            </span>
                          ) : isPrepaid ? (
                            <span className="badge bg-success">
                              Already Pre-Paid
                            </span>
                          ) : isSelected ? (
                            <span className="badge bg-primary">Selected</span>
                          ) : (
                            <span className="badge bg-light text-dark">
                              Select
                            </span>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

          </div>

          {/* ================= REMARKS ================= */}

          <div className="row">

            <div className="col-md-6 mb-3">

              <label className="form-label">
                Remarks
              </label>

              <input
                type="text"
                className="form-control"
                placeholder="Enter remarks"
                value={remarks}
                onChange={(e) =>
                  setRemarks(e.target.value)
                }
                disabled={prePayAutoPoolLoading}
              />

            </div>

          </div>

          {/* ================= SUMMARY ================= */}

          {currentPool &&
            paidPool &&
            selectedPool &&
            amount > 0 && (
              <div className="alert alert-info mt-2">

                <div className="fw-semibold mb-2">
                  Pre-Payment Summary
                </div>

                <div className="d-flex justify-content-between">
                  <span>User ID</span>
                  <strong>{userId}</strong>
                </div>

                <div className="d-flex justify-content-between">
                  <span>Current Pool</span>
                  <strong>
                    Pool {currentPool}
                  </strong>
                </div>

                <div className="d-flex justify-content-between">
                  <span>Paid Pool</span>
                  <strong>
                    Pool {paidPool}
                  </strong>
                </div>

                <hr />

                <div className="d-flex justify-content-between">
                  <span>Amount</span>
                  <strong>
                    ${amount.toFixed(2)}
                  </strong>
                </div>

              </div>
            )}

          {/* ================= BUTTONS ================= */}

          <div className="d-flex justify-content-end gap-2 mt-4">

            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={prePayAutoPoolLoading}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2"
              disabled={
                prePayAutoPoolLoading ||
                !userId ||
                !currentPool ||
                !paidPool ||
                !selectedPool ||
                selectedPool?.alreadyPrePaid ||
                !amount
              }
              onClick={handleSubmit}
            >

              {prePayAutoPoolLoading ? (
                <>
                  <FiLoader className="spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FiDollarSign />
                  Pre-Pay ${amount.toFixed(2)}
                </>
              )}

            </button>

          </div>

        </div>

      </div>

      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>

    </div>
  );
};

export default PrePayAutoPool;